import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, Smartphone, Award, Wrench, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { companyInfo, features, services as mockServices } from '../data/mock';
import { getServices } from '../services/productApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [searchParams] = useSearchParams();
  const prefilledProduct = searchParams.get('produkt') || '';
  const [services, setServices] = useState(mockServices);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: prefilledProduct ? `Anfrage zu ${prefilledProduct}` : '',
    message: prefilledProduct
      ? `Guten Tag,\n\nich interessiere mich für den ${prefilledProduct} und bitte um weitere Informationen (Verfügbarkeit, Lieferzeit, Konditionen).\n\nVielen Dank!`
      : '',
    website: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesData = await getServices();
        if (servicesData?.length > 0) {
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data && response.data.status === 'sent') {
        toast.success('Vielen Dank für Ihre Nachricht! Wir melden uns innerhalb von 24 Stunden bei Ihnen.', {
          duration: 5000
        });
      } else {
        toast.success('Ihre Nachricht wurde gespeichert. Wir bearbeiten Ihre Anfrage schnellstmöglich und melden uns innerhalb von 24 Stunden.', {
          duration: 5000
        });
      }
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        website: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error.response && error.response.status === 429) {
        toast.error('Zu viele Anfragen. Bitte warten Sie einige Minuten und versuchen Sie es erneut.', {
          duration: 5000
        });
      } else {
        toast.error('Es gab einen Fehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.', {
          duration: 5000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Desktop: Kacheln links – Video mitte – Kacheln rechts */}
      <section className="relative bg-white pt-2">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          
          {/* Desktop Layout: 3 Spalten */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-[1fr_2fr_1fr] gap-5 xl:gap-6">
              
              {/* Linke Spalte: 2 Kacheln */}
              <div className="flex flex-col gap-4 h-[400px] xl:h-[450px]">
                {/* Kachel 1: Telefon */}
                <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                  <CardContent className="h-full flex flex-col items-center justify-center text-center p-5">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-3">
                      <Phone className="text-red-600" size={26} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Rufen Sie uns an</h3>
                    <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-sm text-red-600 font-medium hover:underline">
                      {companyInfo.phone}
                    </a>
                    <a href={`tel:${companyInfo.mobile.replace(/\s/g, '')}`} className="text-sm text-gray-600 mt-1 hover:text-red-600">
                      Mobil: {companyInfo.mobile}
                    </a>
                  </CardContent>
                </Card>

                {/* Kachel 2: E-Mail */}
                <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                  <CardContent className="h-full flex flex-col items-center justify-center text-center p-5">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-3">
                      <Mail className="text-red-600" size={26} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Schreiben Sie uns</h3>
                    <a href={`mailto:${companyInfo.email}`} className="text-sm text-red-600 font-medium hover:underline">
                      {companyInfo.email}
                    </a>
                  </CardContent>
                </Card>
              </div>

              {/* Mittlere Spalte: Video */}
              <div className="relative w-full overflow-hidden rounded-xl h-[400px] xl:h-[450px]">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster="/videos/hero-poster.jpg"
                  className="w-full h-full object-cover object-center"
                  webkit-playsinline="true"
                >
                  <source src="/videos/hero.mp4" type="video/mp4" />
                </video>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end">
                  <div className="px-6 pb-6 w-full text-center">
                    <h1 className="text-3xl xl:text-4xl font-bold text-white mb-2">Kontakt</h1>
                    <p className="text-sm xl:text-base text-gray-200">
                      Wir freuen uns auf Ihre Nachricht
                    </p>
                  </div>
                </div>
              </div>

              {/* Rechte Spalte: 2 Kacheln */}
              <div className="flex flex-col gap-4 h-[400px] xl:h-[450px]">
                {/* Kachel 3: Adresse */}
                <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                  <CardContent className="h-full flex flex-col items-center justify-center text-center p-5">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-3">
                      <MapPin className="text-red-600" size={26} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Besuchen Sie uns</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {companyInfo.address}
                    </p>
                  </CardContent>
                </Card>

                {/* Kachel 4: Öffnungszeiten */}
                <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                  <CardContent className="h-full flex flex-col items-center justify-center text-center p-5">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-3">
                      <Clock className="text-red-600" size={26} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Öffnungszeiten</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Mo–Fr: 08:00–18:00 Uhr<br />
                      Sa: Nach Vereinbarung
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Unterer Banner */}
            <div className="mt-5 xl:mt-6">
              <Card className="rounded-xl overflow-hidden bg-gradient-to-r from-red-600 to-red-700 shadow-lg border-0">
                <CardContent className="py-6 xl:py-8">
                  <div className="flex items-center justify-center">
                    <div className="w-14 h-14 xl:w-16 xl:h-16 bg-white/20 rounded-full flex items-center justify-center mr-5">
                      <Send className="text-white" size={28} />
                    </div>
                    <div className="text-center">
                      <h3 className="text-2xl xl:text-3xl font-bold text-white mb-1">Kontaktieren Sie uns</h3>
                      <p className="text-base xl:text-lg text-red-100">
                        Haben Sie Fragen oder benötigen Sie ein individuelles Angebot? Wir freuen uns auf Ihre Nachricht!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Mobile/Tablet Layout: Video volle Breite */}
          <div className="lg:hidden">
            <div className="relative w-full overflow-hidden rounded-xl">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/hero-poster.jpg"
                className="w-full h-[280px] sm:h-[400px] md:h-[500px] object-cover object-center"
                webkit-playsinline="true"
              >
                <source src="/videos/hero.mp4" type="video/mp4" />
              </video>
              
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent flex items-end">
                <div className="px-6 sm:px-12 pb-6 sm:pb-10">
                  <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-4">Kontakt</h1>
                  <p className="text-sm sm:text-lg text-gray-200">
                    Haben Sie Fragen oder benötigen Sie ein individuelles Angebot?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Kontakt-Kacheln (nur Mobile/Tablet) */}
      <section className="py-6 bg-white lg:hidden">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 gap-4">
            <Card className="rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
              <CardContent className="pt-5 pb-5 text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="text-red-600" size={22} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Anrufen</h3>
                <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-xs text-red-600 font-medium">
                  {companyInfo.phone}
                </a>
              </CardContent>
            </Card>

            <Card className="rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
              <CardContent className="pt-5 pb-5 text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="text-red-600" size={22} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">E-Mail</h3>
                <a href={`mailto:${companyInfo.email}`} className="text-xs text-red-600 font-medium">
                  {companyInfo.email}
                </a>
              </CardContent>
            </Card>

            <Card className="rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
              <CardContent className="pt-5 pb-5 text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MapPin className="text-red-600" size={22} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Adresse</h3>
                <p className="text-xs text-gray-600">{companyInfo.address}</p>
              </CardContent>
            </Card>

            <Card className="rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
              <CardContent className="pt-5 pb-5 text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="text-red-600" size={22} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Öffnungszeiten</h3>
                <p className="text-xs text-gray-600">Mo–Fr: 08:00–18:00</p>
              </CardContent>
            </Card>
          </div>

          {/* Roter Banner Mobile */}
          <div className="mt-4 bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-5 text-center text-white shadow-xl">
            <div className="flex items-center justify-center mb-3">
              <Send className="text-white mr-3" size={28} />
              <h3 className="text-xl font-bold">Kontaktieren Sie uns</h3>
            </div>
            <p className="text-sm text-red-100">
              Wir freuen uns auf Ihre Nachricht und melden uns schnellstmöglich!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {/* Contact Form */}
            <div className="lg:col-span-2 flex">
              <Card className="border-2 border-gray-200 shadow-lg w-full">
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="text-lg sm:text-xl md:text-2xl">Senden Sie uns eine Nachricht</CardTitle>
                  <CardDescription className="text-sm">
                    Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 md:p-6 pt-0">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Ihr Name"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm">E-Mail *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="ihre@email.de"
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm">Telefon</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+49 123 456789"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-sm">Betreff *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          placeholder="Worum geht es?"
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-sm">Nachricht *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Ihre Nachricht an uns..."
                        rows={5}
                        className="text-sm"
                      />
                    </div>

                    {/* Honeypot field */}
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      style={{ display: 'none' }}
                      tabIndex="-1"
                      autoComplete="off"
                    />

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-red-600 hover:bg-red-700 text-white"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                      <Send className="ml-2" size={18} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information Sidebar */}
            <div className="flex flex-col gap-4">
              <Card className="border-2 border-gray-200 shadow-lg flex-1">
                <CardHeader className="p-4">
                  <CardTitle className="text-lg">Kontaktinformationen</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-red-600" size={18} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Festnetz</h3>
                      <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-gray-600 hover:text-red-600 transition-colors text-sm font-medium">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Smartphone className="text-red-600" size={18} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Mobil</h3>
                      <a href={`tel:${companyInfo.mobile.replace(/\s/g, '')}`} className="text-gray-600 hover:text-red-600 transition-colors text-sm font-medium">
                        {companyInfo.mobile}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-red-600" size={18} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-0.5">E-Mail</h3>
                      <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-red-600 transition-colors text-sm break-all">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-red-600" size={18} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Adresse</h3>
                      <p className="text-gray-600 text-sm">{companyInfo.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="text-red-600" size={18} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Öffnungszeiten</h3>
                      <p className="text-gray-600 text-xs">
                        Mo-Fr: 08:00 - 18:00 Uhr<br />
                        Sa: Nach Vereinbarung
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 shadow-lg bg-gray-50 flex-shrink-0">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 text-sm mb-2">Schnelle Antwort gewünscht?</h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Bei dringenden Anfragen erreichen Sie uns am schnellsten telefonisch.
                  </p>
                  <div className="space-y-2">
                    <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="block">
                      <Button className="w-full bg-red-600 hover:bg-red-700 text-white text-sm">
                        <Phone className="mr-2" size={16} />
                        Festnetz anrufen
                      </Button>
                    </a>
                    <a href={`tel:${companyInfo.mobile.replace(/\s/g, '')}`} className="block">
                      <Button variant="outline" className="w-full border-red-600 text-red-600 hover:bg-red-50 text-sm">
                        <Smartphone className="mr-2" size={16} />
                        Mobil anrufen
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Einheitlicher Info-Bereich: 4 Vorteile + 3 Leistungen + CTA */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* 4 Vorteile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const IconComponent = {
                  Award,
                  Wrench,
                  Clock,
                  HeadphonesIcon: Phone
                }[feature.icon];
                
                return (
                  <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                          <IconComponent className="text-red-600" size={24} />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* 3 Leistungen */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        {service.id === 1 && <Award className="text-red-600" size={24} />}
                        {service.id === 2 && <Phone className="text-red-600" size={24} />}
                        {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                      <ul className="space-y-1 text-left w-full">
                        {service.features.slice(0, 3).map((feat, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm">
                            <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-700">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA */}
            <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <ArrowRight className="text-red-600" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl mb-2">Bereit für Ihr Projekt?</h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">
                    Entdecken Sie unser Sortiment an professionellen Baumaschinen.
                  </p>
                  <Link to="/">
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                      Zur Startseite
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Zwei Videos am Ende */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video2-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>

            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video3-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
